import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import { COLORS } from '../styles';
import { Size, Testable } from '../types';
import { InputFieldStyling, InputWrapper, ErrorMessage } from './styling';
import { Z_INDEXES } from '../styles';
import { useFocusVisibleRef } from '../common';
import { IconButton } from '../Button';
import { SystemIcons } from '../icons';

export type PasswordFieldProps =
  Testable & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'id' | 'onChange' | 'readOnly' | 'required' | 'placeholder' | 'autoComplete' | 'value' | 'disabled' | 'tabIndex' | 'type'>
  & {
  /**
   * Required. The ID of the password field.
   */
  id: string;

  /**
   * Optional. A boolean indicating whether the password field is disabled.
   */
  disabled?: boolean;

  /**
   * Optional. A function to be called when the value of the password field changes.
   */
  onChange?: (text: string) => void;

  /**
   * Optional. A boolean indicating whether the password field is invalid.
   */
  invalid?: boolean;

  /**
   * Optional. The current value of the password field.
   */
  value?: string;

  /**
   * Optional. The validation message to be displayed when the password field is invalid.
   */
  validationMessage?: string;

  /**
   * Optional. The autocomplete attribute for the password field.
   */
  autoComplete?: string;

  /**
   * Optional. The placeholder text for the password field.
   */
  placeholder?: string;

  /**
   * Optional. A boolean indicating whether the password field is required.
   */
  required?: boolean;

  /**
   * Optional. A boolean indicating whether the password field is read-only.
   */
  readOnly?: boolean;

  /**
   * Optional. The size of the password field. Can be 'Small' or 'Medium'.
   */
  size?: Size.Small | Size.Medium;

  /**
   * Optional. The margin of the password field. Can be any valid CSS margin value.
   */
  margin?: string;
};

const StyledPassSwitch = styled.div`
  z-index: ${Z_INDEXES.badge};
  right: 0;
  position: absolute;
`;

const PasswordRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(({
                                          id,
                                          disabled,
                                          onChange,
                                          invalid,
                                          value,
                                          validationMessage,
                                          autoComplete,
                                          placeholder,
                                          required,
                                          readOnly,
                                          size,
                                          margin,
                                          className,
                                          dataTestId,
                                          name = 'password',
                                          ...rest
                                        }: PasswordFieldProps, ref) => {
  const [passwordHidden, setPasswordHidden] = React.useState<Boolean>(true);
  const inputRef = useFocusVisibleRef();
  const theme = useTheme();

  React.useImperativeHandle(ref, () => inputRef.current, [inputRef]);

  const handleKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      setPasswordHidden(!passwordHidden);
    }
  };

  const cls = `${invalid ? 'invalid' : ''}  ${size || ''} ${className}`;

  return (
    <>
      <InputWrapper $disabled={disabled} $readOnly={readOnly} $margin={margin}>
        <PasswordRow>
            <InputFieldStyling
              id={id}
              data-testid={dataTestId}
              ref={inputRef}
              type={passwordHidden ? 'password' : 'text'}
              name={name}
              value={value}
              className={cls}
              tabIndex={disabled || readOnly ? -1 : 0}
              autoComplete={autoComplete}
              $activeErrorMessage={!!validationMessage}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              readOnly={readOnly}
              onChange={(e: any) => onChange && onChange(e?.target?.value || '')}
              {...rest}
            />
            <StyledPassSwitch className={size ? size : ''}>
              {!disabled && !readOnly ? (
                passwordHidden ? (
                  <IconButton
                    id={`${id}_Visible`}
                    iconColor={'#666666'}
                    variant={'secondary'}
                    shape={'circular'}
                    onKeyPress={handleKeyDown}
                    action={() => setPasswordHidden(!passwordHidden)}>
                    <SystemIcons.VisibleOff />
                  </IconButton>
                ) : (
                  <IconButton
                    id={`${id}_NotVisible`}
                    iconColor={'#666666'}
                    variant={'secondary'}
                    shape={'circular'}
                    onKeyPress={handleKeyDown}
                    action={() => setPasswordHidden(!passwordHidden)}>
                    <SystemIcons.VisibleOn />
                  </IconButton>
                )
              ) : null}
            </StyledPassSwitch>
        </PasswordRow>
      </InputWrapper>
      {validationMessage && (
        <ErrorMessage className={size || ''}>
          <SystemIcons.TechnicalWarning color={COLORS.generateToken({ componentType: 'text', defaultVariant: 'critical' }, theme)} />
          <span>{validationMessage}</span>
        </ErrorMessage>
      )}
    </>
  );
});

export default PasswordField;
